import { Stack } from "@mui/material";
import { chain, groupBy } from "lodash-es";
import { useMemo, useState } from "react";

import { Mapper } from "@/common/mapping/mapper";
import { GenericDamage } from "@/common/ts/visualModel";
import { VehicleArea, VehicleProjection } from "@/core/api/generated/v1";

import { VisualModelViewer } from "../VisualModelViewer";
import { GenericDamageList } from "./GenericDamageList";
import { GenericDamageViewModal } from "./GenericDamageViewModal";

export interface VisualModelViewerOfGenericDamagesProps<
  TDamage extends GenericDamage = GenericDamage,
> {
  damages: TDamage[];
  withDamageList: boolean;
}

/** Displays damages on different visual models. */
export function VisualModelViewerOfGenericDamages<TDamage extends GenericDamage = GenericDamage>({
  damages,
  withDamageList,
}: VisualModelViewerOfGenericDamagesProps<TDamage>) {
  const [damageToView, setDamageToView] = useState<TDamage | null>(null);

  const damagesByVisualModelIdMap = useMemo(
    () =>
      groupBy(
        damages?.filter((x) => x.visualModelId),
        (x) => x.visualModelId || "",
      ),
    [damages],
  );

  const pointsByVisualModelIdMap = useMemo(
    () =>
      chain(
        Object.entries(damagesByVisualModelIdMap).map(([visualModelId, damages2]) => ({
          visualModelId,
          damages: damages2,
        })),
      )
        .keyBy((x) => x.visualModelId)
        .mapValues((x) =>
          x.damages
            .map((damage) => Mapper.visualModel.mapGenericDamageToVisualModelPoint(damage)!)
            .filter(Boolean),
        )
        .value(),

    [damagesByVisualModelIdMap],
  );

  return (
    <Stack spacing={2}>
      {/* Visual models */}
      {Object.keys(damagesByVisualModelIdMap).map((visualModelId, i) => (
        <VisualModelViewer
          key={visualModelId || i}
          visualModelId={visualModelId}
          visualModel={undefined}
          initialMainImageSelector={{
            area: VehicleArea.Exterior,
            projection: VehicleProjection.Front,
          }}
          points={pointsByVisualModelIdMap[visualModelId || ""] || []}
          mainViewerProps={{
            onPointClicked: (params) => {
              if (params.point.data) {
                setDamageToView(params.point.data);
              }
            },
          }}
        />
      ))}

      {/* Damage list */}
      {withDamageList && (
        <GenericDamageList
          damages={damages || []}
          isViewEnabled
          isUpdateEnabled={false}
          isDeleteEnabled={false}
          onView={(damage) => {
            setDamageToView(damage);
          }}
          onUpdate={undefined}
          onDelete={undefined}
        />
      )}

      {/* Damage view modal */}
      {damageToView && (
        <GenericDamageViewModal
          open={!!damageToView}
          onClose={() => {
            setDamageToView(null);
          }}
          viewProps={{
            damage: damageToView,
          }}
        />
      )}
    </Stack>
  );
}
